// =============================================================================
// Bureau de Matthieu — vue par défaut « Réponses prêtes (N) » (T-281). TOUTES les fiches « à traiter » qui portent une réponse prête,
// la réponse visible sans ouvrir la fiche, traitées à la chaîne : une ligne = un mail = UNE décision par clic.
// Règle du Bureau : aucune validation groupée pour l'externe. Pas de case « tout sélectionner », pas de bouton « tout valider ».
// Seule exception (T-442, décision D-5) : le lot de 50 de l'Offre d'aides, dont le texte a été validé UNE fois (panneau en tête, composant à part).
// Les envois passent par les mêmes fonctions d'API que la fiche (version + empreinte du texte vu) : le serveur décide capture / réel.
// Couleurs : tokens var(--oh-*) uniquement (2 modes).
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Inbox, MailOpen } from "lucide-react";

import { OhField, OhInput, OhStatusNote } from "../../../components/ui/oh-charte";
import * as api from "./bureauAmoApi";
import Bouton from "./boutonBureau";
import OffreAidesLot from "./OffreAidesLot";
import { PastilleMode, Puce, SqueletteListe, TYPE_EXPEDITEUR, URGENCE_PUCE } from "./BureauMatthieuUi";

export const DELAI_RELECTURE_MS = 800;
const DETAIL_AFFICHE = [422, 429, 502, 503];
const muted = { color: "var(--oh-text-muted)", fontSize: 13 };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toLocaleDateString("fr-FR");
};

// Une réponse est « longue » au-delà de 3 lignes ou 240 caractères : l'aperçu la tronque, il faut la déplier avant d'envoyer.
const estLongue = (t) => (t || "").split("\n").length > 3 || (t || "").length > 240;
const CLAMP3 = { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" };
const CLAMP1 = { display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" };

const K = ({ children }) => (
  <kbd style={{ padding: "0 6px", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", background: "var(--oh-bg-surface)", color: "var(--oh-text)", fontFamily: "inherit", fontSize: 12 }}>{children}</kbd>
);

const estSaisie = (el) => {
  if (!el || !el.tagName) return false;
  const t = el.tagName.toLowerCase();
  return t === "input" || t === "textarea" || t === "select" || el.isContentEditable === true;
};

function LigneReponse({ l, focus, occupe, verrou, panneau, setPanneau, confirme, setConfirme, depliee, setDepliee, mailOuvert, setMailOuvert,
  mail, MailRecu, onFocus, onValider, onEnvoyerModifie, onClasser, onOuvrir, onOuvrirWebmail, onRechargerMail, refLigne }) {
  const urgence = URGENCE_PUCE[l.urgence];
  const nom = l.client || l.expediteur_adresse || "Expéditeur inconnu";
  const contexte = [l.client ? l.expediteur_adresse : null, l.instructeur && l.instructeur !== l.expediteur_adresse ? `Instructeur : ${l.instructeur}` : null,
    l.date_mail ? `reçu le ${fmtDate(l.date_mail)}` : null].filter(Boolean).join(" · ");
  const longue = estLongue(l.texte_propose);
  const toutVoir = focus || depliee;
  const editer = panneau?.id === l.id && panneau.type === "modifier";
  const classer = panneau?.id === l.id && panneau.type === "classer";
  const manqueConfirmation = Boolean(l.reply_to_different) && !confirme;
  const destinataireConfirme = l.reply_to_different && confirme ? l.destinataire_propose : undefined;
  const bloque = occupe || !verrou;
  const modifie = editer && (panneau.texte !== (l.texte_propose || "") || panneau.objet !== (l.objet_propose || ""));
  const id = l.id;

  return (
    <li ref={refLigne} tabIndex={-1} aria-current={focus ? "true" : undefined} data-testid={`rp-ligne-${id}`} data-focus={focus ? "oui" : "non"}
      onFocus={(e) => { if (e.target === e.currentTarget) onFocus(id); }} onClick={() => !focus && onFocus(id)}
      style={{ listStyle: "none", display: "grid", gap: "var(--oh-space-3)", padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", outline: "none",
        border: focus ? "1px solid var(--oh-accent)" : "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}>
      <div className="flex flex-wrap items-center gap-2">
        {urgence ? <Puce color={urgence[0]}>{urgence[1]}</Puce> : l.urgence ? <Puce color="blue">{l.urgence}</Puce> : null}
        <Puce color="purple">{TYPE_EXPEDITEUR[l.expediteur_type] || l.expediteur_type || "Autre"}</Puce>
        {l.lead && <Puce color="green">Lead</Puce>}
        {l.reponse_recue && <Puce color="cyan">Réponse reçue</Puce>}
        {l.reply_to_different && <Puce color="red">Adresse de réponse différente</Puce>}
      </div>

      <div style={{ display: "grid", gap: "var(--oh-space-1)" }}>
        <p style={{ margin: 0, fontWeight: 500, fontSize: 14, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid={`rp-expediteur-${id}`}>{nom}</p>
        {contexte && <p style={{ ...muted, margin: 0, overflowWrap: "anywhere" }}>{contexte}</p>}
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid={`rp-objet-${id}`}>{l.objet_origine || "Sans objet"}</h3>
        <p style={{ margin: 0, fontSize: 14, color: "var(--oh-text-secondary)", overflowWrap: "anywhere", ...(toutVoir ? {} : CLAMP1) }} data-testid={`rp-demande-${id}`}>
          {l.demande || "Demande non renseignée."}
        </p>
      </div>

      <div style={{ display: "grid", gap: "var(--oh-space-2)", padding: "var(--oh-space-3)", background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)" }}>
        <p style={{ ...muted, margin: 0, overflowWrap: "anywhere" }} data-testid={`rp-destinataire-${id}`}>Réponse proposée · à {l.destinataire_propose || "—"}</p>
        {editer ? (
          <div className="space-y-2">
            <OhField label="Objet" htmlFor={`rp-objet-champ-${id}`}>
              <OhInput id={`rp-objet-champ-${id}`} data-testid={`rp-modif-objet-${id}`} value={panneau.objet} disabled={occupe}
                onChange={(e) => setPanneau({ ...panneau, objet: e.target.value })} />
            </OhField>
            <OhField label="Texte de la réponse" htmlFor={`rp-texte-champ-${id}`}>
              <textarea id={`rp-texte-champ-${id}`} data-testid={`rp-modif-texte-${id}`} value={panneau.texte} rows={8} disabled={occupe}
                onChange={(e) => setPanneau({ ...panneau, texte: e.target.value })}
                style={{ width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)", background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, lineHeight: 1.6 }} />
            </OhField>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--oh-text)", whiteSpace: "pre-wrap", overflowWrap: "anywhere", ...(toutVoir ? {} : CLAMP3) }}
            data-testid={`rp-texte-${id}`}>
            {l.texte_propose}
          </p>
        )}
        {longue && !editer && !focus && (
          <div>
            <Bouton data-testid={`rp-deplier-${id}`} aria-expanded={depliee} onClick={(e) => { e.stopPropagation(); setDepliee(id, !depliee); }}>
              {depliee ? "Réduire la réponse" : "Afficher toute la réponse"}
            </Bouton>
          </div>
        )}
      </div>

      {l.reply_to_different && (
        <OhStatusNote variant="error" data-testid={`rp-alerte-reply-to-${id}`} title="Adresse de réponse différente">
          <div className="space-y-2">
            <p style={{ margin: 0 }}>Le mail demande qu'on réponde à une autre adresse que l'expéditeur. Le message part à l'adresse « à » ci-dessus, jamais à l'adresse de réponse.</p>
            <label className="flex items-center gap-2" style={{ color: "var(--oh-text)", fontSize: 14 }}>
              <input type="checkbox" data-testid={`rp-confirmer-destinataire-${id}`} checked={confirme} onChange={(e) => setConfirme(id, e.target.checked)} />
              Je confirme que le destinataire est bien {l.destinataire_propose}
            </label>
          </div>
        </OhStatusNote>
      )}

      {classer && (
        <div className="space-y-2" data-testid={`rp-refus-${id}`}>
          <OhField label="Motif du classement" htmlFor={`rp-motif-${id}`} hint="Entre 3 et 500 caractères. Aucune réponse n'est envoyée.">
            <OhInput id={`rp-motif-${id}`} data-testid={`rp-motif-${id}`} value={panneau.motif} maxLength={500} disabled={occupe} autoFocus
              onChange={(e) => setPanneau({ ...panneau, motif: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter" && panneau.motif.trim().length >= 3 && !occupe) onClasser(l, panneau.motif.trim()); }} />
          </OhField>
          <div className="flex flex-wrap gap-2">
            <Bouton data-testid={`rp-refus-confirmer-${id}`} disabled={occupe || panneau.motif.trim().length < 3} onClick={() => onClasser(l, panneau.motif.trim())}>
              Confirmer le classement
            </Bouton>
            <Bouton data-testid={`rp-refus-annuler-${id}`} disabled={occupe} onClick={() => setPanneau(null)}>Annuler</Bouton>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid={`rp-actions-${id}`}>
        {editer ? (
          <>
            <Bouton type="secondaire" data-testid={`rp-modif-envoyer-${id}`} disabled={bloque || !modifie || !panneau.texte.trim() || manqueConfirmation}
              title={!modifie ? "Aucune modification" : manqueConfirmation ? "Confirmez d'abord le destinataire" : undefined}
              onClick={() => onEnvoyerModifie(l, panneau, destinataireConfirme)}>
              {occupe ? "Envoi en cours…" : "Envoyer la version modifiée"}
            </Bouton>
            <Bouton data-testid={`rp-modif-annuler-${id}`} disabled={occupe} onClick={() => setPanneau(null)}>Annuler la modification</Bouton>
          </>
        ) : (
          <>
            <Bouton type="secondaire" data-testid={`rp-valider-${id}`} disabled={bloque || manqueConfirmation} aria-busy={occupe}
              title={manqueConfirmation ? "Confirmez d'abord le destinataire" : !verrou ? "Relisez la réponse avant de valider" : undefined}
              onClick={() => onValider(l, destinataireConfirme)}>
              {occupe ? "Envoi en cours…" : "Valider et envoyer"}
            </Bouton>
            <Bouton data-testid={`rp-modifier-${id}`} disabled={occupe} onClick={() => { onFocus(id); setPanneau({ id, type: "modifier", objet: l.objet_propose || "", texte: l.texte_propose || "" }); }}>Modifier</Bouton>
            <Bouton data-testid={`rp-classer-${id}`} disabled={occupe} onClick={() => { onFocus(id); setPanneau({ id, type: "classer", motif: "" }); }}>Classer</Bouton>
          </>
        )}
        <Bouton data-testid={`rp-ouvrir-${id}`} disabled={occupe} onClick={() => onOuvrir(id)}>
          <ExternalLink className="w-4 h-4 inline mr-1" aria-hidden="true" />Ouvrir
        </Bouton>
        <Bouton data-testid={`rp-mail-bascule-${id}`} aria-expanded={mailOuvert} style={{ marginLeft: "auto" }} onClick={() => setMailOuvert(id, !mailOuvert)}>
          <MailOpen className="w-4 h-4 inline mr-1" aria-hidden="true" />{mailOuvert ? "Masquer le mail d'origine" : "Voir le mail d'origine"}
        </Bouton>
      </div>

      {mailOuvert && MailRecu && (
        <div data-testid={`rp-mail-${id}`}>
          <MailRecu fiche={l} mail={mail || { chargement: true, erreur: null, donnees: null }} onRecharger={() => onRechargerMail(id)} onOuvrirWebmail={onOuvrirWebmail} />
        </div>
      )}
    </li>
  );
}

export default function ReponsesPretes({ MailRecu, onOuvrir, onOuvrirWebmail, onCompte, filtre = null, delaiRelectureMs = DELAI_RELECTURE_MS }) {
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const [lignes, setLignes] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [verrou, setVerrou] = useState(delaiRelectureMs <= 0);
  const [panneau, setPanneau] = useState(null);
  const [confirmes, setConfirmes] = useState({});
  const [depliees, setDepliees] = useState({});
  const [mailsOuverts, setMailsOuverts] = useState({});
  const [mails, setMails] = useState({});
  const [traitees, setTraitees] = useState({ envoyees: 0, classees: 0 });
  const [indice, setIndice] = useState("");
  const refs = useRef({});
  const numero = useRef(0);
  const focaliserDom = useRef(false);

  const charger = useCallback(async (garderFocus) => {
    const n = ++numero.current;
    setEtat((e) => ({ ...e, chargement: true, erreur: null }));
    try {
      const donnees = await api.listerReponsesPretes(filtre === "pret_a_envoyer" ? { pret_a_envoyer: true } : undefined);
      if (n !== numero.current) return;
      const items = donnees.items || [];
      setEtat({ chargement: false, erreur: null, donnees });
      setLignes(items);
      setPanneau(null);
      setFocusId((cur) => (garderFocus && items.some((i) => i.id === cur) ? cur : (items[0]?.id ?? null)));
    } catch (e) {
      if (n === numero.current) setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, [filtre]);
  useEffect(() => { charger(false); }, [charger]);

  useEffect(() => { if (onCompte && etat.donnees) onCompte(lignes.length); }, [lignes.length, etat.donnees, onCompte]);

  // Verrou de relecture : tout envoi est désactivé un court instant après chaque changement de ligne en focus ou de liste (double-clic, touche répétée).
  useEffect(() => {
    if (delaiRelectureMs <= 0 || !focusId) { setVerrou(true); return undefined; }
    setVerrou(false);
    const t = setTimeout(() => setVerrou(true), delaiRelectureMs);
    return () => clearTimeout(t);
  }, [focusId, lignes.length, delaiRelectureMs]);

  useEffect(() => {
    const el = refs.current[focusId];
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [focusId]);

  useEffect(() => {
    const el = focaliserDom.current ? refs.current[focusId] : null;
    if (el && typeof el.focus === "function") { el.focus({ preventScroll: true }); focaliserDom.current = false; }
  }, [focusId, lignes.length]);

  const indexFocus = lignes.findIndex((l) => l.id === focusId);
  const ligneFocus = indexFocus >= 0 ? lignes[indexFocus] : null;

  const focuser = useCallback((id) => {
    setFocusId(id);
    setPanneau((p) => (p && p.id !== id ? null : p));
    setIndice("");
  }, []);

  const apres = (id, nature) => {
    focaliserDom.current = true;
    const i = lignes.findIndex((l) => l.id === id);
    const suivante = lignes[i + 1] || lignes[i - 1] || null;
    setLignes((ls) => ls.filter((l) => l.id !== id));
    setFocusId(suivante ? suivante.id : null);
    setPanneau(null);
    setTraitees((t) => ({ ...t, [nature]: t[nature] + 1 }));
  };

  const traiter = async (l, action, succes, nature) => {
    if (occupe) return;
    setOccupe(l.id);
    setIndice("");
    try {
      const retour = await action();
      toast.success(typeof succes === "function" ? succes(retour) : succes);
      apres(l.id, nature);
    } catch (e) {
      if (e.status === 409) {
        toast.error("La fiche a changé entre-temps : la liste est rechargée, relisez avant de décider.");
        await charger(true);
      } else if (DETAIL_AFFICHE.includes(e.status)) {
        toast.error(typeof e.detail === "string" ? e.detail : "Données refusées par le serveur.");
        if (e.status === 502) await charger(true);
      } else {
        toast.error("Enregistrement impossible.");
      }
    } finally {
      setOccupe(null);
    }
  };

  const messageEnvoi = (r) => (r?.mode === "reel" ? "Mail envoyé depuis amo@." : "Capturé : rien n'est parti (mode capture).");

  const valider = (l, destinataireConfirme) => {
    if (l.id !== focusId && estLongue(l.texte_propose)) {          // jamais d'envoi d'une réponse que l'écran n'a montrée qu'en aperçu
      focuser(l.id);
      toast.info("Réponse complète affichée : relisez, puis validez.");
      return;
    }
    traiter(l, () => api.valider(l.id, l.version, l.texte_sha256, destinataireConfirme), messageEnvoi, "envoyees");
  };
  const envoyerModifie = (l, p, destinataireConfirme) =>
    traiter(l, () => api.modifierEtEnvoyer(l.id, l.version, p.texte, p.objet !== (l.objet_propose || "") ? p.objet : undefined, destinataireConfirme), messageEnvoi, "envoyees");
  const classer = (l, motif) => traiter(l, () => api.enregistrerRefus(l.id, l.version, motif), "Fiche classée sans réponse.", "classees");

  const validerLaFocus = () => {
    if (!ligneFocus) return;
    if (ligneFocus.reply_to_different && !confirmes[ligneFocus.id]) { setIndice("Confirmez d'abord le destinataire (case de la ligne)."); return; }
    valider(ligneFocus, ligneFocus.reply_to_different ? ligneFocus.destinataire_propose : undefined);
  };

  const bouger = (delta) => {
    if (!lignes.length) return;
    const i = Math.max(0, Math.min(lignes.length - 1, (indexFocus < 0 ? 0 : indexFocus) + delta));
    focuser(lignes[i].id);
    const el = refs.current[lignes[i].id];
    if (el && typeof el.focus === "function") el.focus({ preventScroll: true });
  };

  // Raccourcis : ignorés dans un champ de saisie, avec un modificateur, en répétition de touche, ou pendant un envoi / le verrou de relecture.
  const clavier = useRef(null);
  clavier.current = (e) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || estSaisie(e.target)) return;
    const k = e.key;
    if (k === "ArrowDown" || k === "ArrowUp") { e.preventDefault(); bouger(k === "ArrowDown" ? 1 : -1); return; }
    if (!["v", "V", "m", "M", "c", "C", "Escape"].includes(k)) return;
    if (k === "Escape") { setPanneau(null); return; }
    if (e.repeat || occupe || !ligneFocus) return;
    e.preventDefault();
    if (k.toLowerCase() === "m") { setPanneau({ id: ligneFocus.id, type: "modifier", objet: ligneFocus.objet_propose || "", texte: ligneFocus.texte_propose || "" }); return; }
    if (k.toLowerCase() === "c") { setPanneau({ id: ligneFocus.id, type: "classer", motif: "" }); return; }
    if (panneau) { setIndice("Terminez ou annulez d'abord la modification en cours."); return; }
    if (!verrou) { setIndice("Relisez la réponse, puis validez."); return; }
    validerLaFocus();
  };
  useEffect(() => {
    const h = (e) => clavier.current(e);
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const ouvrirMail = (id, ouvert) => {
    setMailsOuverts((m) => ({ ...m, [id]: ouvert }));
    if (ouvert && !mails[id]) chargerMail(id);
  };
  const chargerMail = (id) => {
    setMails((m) => ({ ...m, [id]: { chargement: true, erreur: null, donnees: null } }));
    api.mailOrigine(id).then(
      (d) => setMails((m) => ({ ...m, [id]: { chargement: false, erreur: null, donnees: d } })),
      (e) => setMails((m) => ({ ...m, [id]: { chargement: false, erreur: e, donnees: null } })),
    );
  };

  const mode = etat.donnees?.mode === "reel" ? "reel" : "capture";
  const restantes = lignes.length;
  const sansConfirmation = Boolean(ligneFocus?.reply_to_different) && !confirmes[ligneFocus?.id];

  if (etat.erreur && [401, 403].includes(etat.erreur.status)) {
    return (
      <OhStatusNote variant="info" data-testid="bureau-amo-module-ferme" title="Bureau de Matthieu non activé">
        Ce module n'est pas activé pour votre entreprise, ou votre rôle ne permet pas de l'ouvrir.
      </OhStatusNote>
    );
  }

  return (
    <div className="space-y-3" data-testid="bureau-amo-reponses-pretes" aria-busy={etat.chargement}>
      {/* T-442 : le lot de l'Offre d'aides (texte validé une fois) — seule exception à « une validation = un mail », posée par la décision D-5 */}
      <OffreAidesLot onChange={() => charger(true)} />
      <div style={{ position: "sticky", top: 0, zIndex: 2, display: "grid", gap: "var(--oh-space-2)", padding: "var(--oh-space-3) var(--oh-space-4)",
        background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)" }} data-testid="rp-chaine">
        <div className="flex flex-wrap items-center gap-3">
          <span style={{ fontWeight: 500, fontSize: 16, color: "var(--oh-text)" }} data-testid="rp-restantes" role="status">
            {restantes} restante{restantes > 1 ? "s" : ""}
          </span>
          <span style={muted} data-testid="rp-traitees">{traitees.envoyees} validée{traitees.envoyees > 1 ? "s" : ""} · {traitees.classees} classée{traitees.classees > 1 ? "s" : ""}</span>
          {etat.donnees && <PastilleMode mode={mode} />}
          <Bouton type="primaire" style={{ marginLeft: "auto" }} data-testid="rp-valider-suivant" aria-busy={occupe !== null}
            disabled={!ligneFocus || occupe !== null || !verrou || Boolean(panneau) || sansConfirmation}
            title={!verrou ? "Relisez la réponse avant de valider" : sansConfirmation ? "Confirmez d'abord le destinataire" : panneau ? "Terminez ou annulez la modification en cours" : undefined}
            onClick={validerLaFocus}>
            {occupe !== null ? "Envoi en cours…" : "Valider et suivant"}
          </Bouton>
        </div>
        <p className="hidden sm:block" style={{ ...muted, margin: 0 }} data-testid="rp-aide-clavier">
          Raccourcis : <K>V</K> valider · <K>M</K> modifier · <K>C</K> classer · <K>↑</K> <K>↓</K> changer de ligne. Une validation = un mail ; rien ne part en groupe.
        </p>
        <p style={{ ...muted, margin: 0, minHeight: 20 }} role="status" aria-live="polite" data-testid="rp-indice">{indice}</p>
      </div>

      {etat.erreur && (
        <OhStatusNote variant="error" data-testid="rp-erreur" title="Chargement impossible">
          <div className="flex flex-wrap items-center gap-2">
            <span>{typeof etat.erreur.detail === "string" ? etat.erreur.detail : "Réessayez dans un instant."}</span>
            <Bouton data-testid="rp-recharger" onClick={() => charger(true)}>Réessayer</Bouton>
          </div>
        </OhStatusNote>
      )}
      {etat.donnees?.tronque && (
        <OhStatusNote variant="info" data-testid="rp-tronque" title="Liste limitée">
          Seules les {etat.donnees.items.length} premières réponses prêtes sont affichées (sur {etat.donnees.total}). Les suivantes apparaîtront au fil du traitement.
        </OhStatusNote>
      )}

      {etat.chargement && !etat.donnees ? <SqueletteListe /> : !etat.erreur && lignes.length === 0 ? (
        <div data-testid="rp-vide" style={{ padding: "var(--oh-space-8) var(--oh-space-4)", textAlign: "center", color: "var(--oh-text-secondary)", fontSize: 14,
          border: "1px dashed var(--oh-border)", borderRadius: "var(--oh-radius-card)" }}>
          <Inbox className="w-5 h-5 inline" aria-hidden="true" style={{ color: "var(--oh-text-muted)" }} />
          <p style={{ margin: "var(--oh-space-2) 0 0", color: "var(--oh-text)", fontWeight: 500 }}>
            {traitees.envoyees + traitees.classees > 0 ? "Tout est traité." : "Aucune réponse prête."}
          </p>
          <p style={{ ...muted, margin: "var(--oh-space-1) 0 0" }}>
            {traitees.envoyees + traitees.classees > 0
              ? `${traitees.envoyees} validée${traitees.envoyees > 1 ? "s" : ""}, ${traitees.classees} classée${traitees.classees > 1 ? "s" : ""}.`
              : "Les réponses préparées par Matthieu apparaîtront ici, prêtes à valider."}
          </p>
        </div>
      ) : (
        <ul style={{ padding: 0, margin: 0, display: "grid", gap: "var(--oh-space-3)" }} aria-label="Réponses prêtes à valider" data-testid="rp-liste">
          {lignes.map((l) => (
            <LigneReponse key={l.id} l={l} focus={l.id === focusId} occupe={occupe === l.id} verrou={verrou && occupe === null}
              panneau={panneau} setPanneau={setPanneau}
              confirme={Boolean(confirmes[l.id])} setConfirme={(id, v) => setConfirmes((c) => ({ ...c, [id]: v }))}
              depliee={Boolean(depliees[l.id])} setDepliee={(id, v) => setDepliees((d) => ({ ...d, [id]: v }))}
              mailOuvert={Boolean(mailsOuverts[l.id])} setMailOuvert={ouvrirMail} mail={mails[l.id]} MailRecu={MailRecu}
              onFocus={focuser} onValider={valider} onEnvoyerModifie={envoyerModifie} onClasser={classer}
              onOuvrir={onOuvrir} onOuvrirWebmail={onOuvrirWebmail} onRechargerMail={chargerMail}
              refLigne={(el) => { if (el) refs.current[l.id] = el; else delete refs.current[l.id]; }} />
          ))}
        </ul>
      )}
    </div>
  );
}
